import { createSfxPlayer, SFX_OUTPUT_GAIN, type SfxName } from '~/utils/sfxSamples'
import { createAudioSettings } from '~/utils/audioSettings'
import { useHaptics } from './useHaptics'

/**
 * The eleven reviewed clips, through the same limiter as music. The
 * original recipes cover cold/offline loads without delaying a press.
 */
// 🔕 SFX mute (v2.32, per Javi's menu): module-level like useMusic's
// flag — one switch for every effect, persisted independently
// of the music mute.
const SFX_MUTE_KEY = 'elemental-dispatch-sfx-muted'
const SFX_VOLUME_KEY = 'elemental-dispatch-sfx-volume'
const settings = createAudioSettings(SFX_VOLUME_KEY, SFX_MUTE_KEY)
const sfxMuted = settings.muted

let player: ReturnType<typeof createSfxPlayer> | null = null
function getPlayer() {
  if (player) return player
  const bus = audioBus()
  if (bus) {
    player = createSfxPlayer(bus.ctx, bus.master, { initialVolume: settings.volume.value * SFX_OUTPUT_GAIN })
  }
  return player
}

function preload() {
  settings.load()
  if (!sfxMuted.value) void getPlayer()?.preload().catch(() => {})
}

function play(name: SfxName) {
  useHaptics().play(name)
  settings.load()
  if (sfxMuted.value || (typeof document !== 'undefined' && document.hidden)) return
  getPlayer()?.play(name)
}

export function useSfx() {
  settings.load()
  return {
    preload,
    stop: () => player?.stopAll(),
    tap: () => play('tap'),
    select: () => play('select'),
    deselect: () => play('deselect'),
    confirm: () => play('confirm'),
    coin: () => play('coin'),
    victory: () => play('victory'),
    goldAppear: () => play('goldAppear'),
    trainingAppear: () => play('trainingAppear'),
    /** 💢 Blunt damage thump (expired missions). */
    hit: () => play('hit'),
    /** 💜/🌟 Rarity stings (v3.22, per Javi): epics and legendaries
     * announce themselves wherever they appear. Pass one rarity or a
     * list — the HIGHEST tier present plays, commons stay silent. */
    rarityAppear: (rarity: string | Array<string | undefined> | undefined) => {
      const list = Array.isArray(rarity) ? rarity : [rarity]
      if (list.includes('legendary')) play('legendary')
      else if (list.includes('epic')) play('epic')
    },
    muted: sfxMuted,
    volume: settings.volume,
    setVolume(value: number) {
      settings.setVolume(value)
      player?.setVolume(settings.volume.value * SFX_OUTPUT_GAIN)
      if (sfxMuted.value) player?.stopAll()
      else preload()
    },
    toggleMute: () => {
      settings.toggleMute()
      player?.setVolume(settings.volume.value * SFX_OUTPUT_GAIN)
      if (sfxMuted.value) player?.stopAll()
      else preload()
    },
  }
}
